import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import EnumSelect from 'components/field/EnumSelect';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Typography from 'components/global/Typography';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { setIsSecondStepFalse, setIsSecondStepTrue } from './registerSlice';

const ReferralViewer = ({ children }) => (
	<Typography style={{ display: 'inline', color: 'var(--blue)' }} variant="p1" fontWeight="500" gutterBottom noWrap>
		{children}
	</Typography>
);

const RegisterSecondStep = () => {
	const [isCountryLoading, setIsCountryLoading] = useState(true);
	const [countries, setCountries] = useState(null);
	const [countryNames, setCountryNames] = useState(null);
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const referralToken = searchParams?.get('referralToken');
	const otoProduct = searchParams?.get('product');

	useEffect(() => {
		dispatch(setIsSecondStepTrue());
		return () => dispatch(setIsSecondStepFalse());
	}, []);

	const fetchLocationInfo = useCallback(async () => {
		await openAxios
			.get(OPEN.GET_ACTIVE_COUNTRIES)
			.then((response) => {
				const { status, data } = response;
				if (status === 200) {
					let filteredCountries = data.countries;
					if (otoProduct === 'importChinaToSaudi') {
						filteredCountries = data.countries.filter((country) => country.ID === 1);
					}

					const currentCountry = filteredCountries.find((country) => country.ID === data.currentCountry.ID);
					setValue('countryID', currentCountry ? currentCountry.ID : 1, {
						shouldValidate: true,
						shouldDirty: true
					});
					setValue('countryCode', currentCountry ? currentCountry.code : 'SA');
					setValue('countryName', currentCountry ? currentCountry.name : 'Kingdom of Saudi Arabia');
					setValue(`refereeAmount`, currentCountry?.refereeAmount);
					setValue('currency', currentCountry?.currency);
					setValue(`referralAvailable`, currentCountry?.referralAvailable);
					setCountries(filteredCountries);
					setCountryNames(filteredCountries.map((item) => item.name));
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			})
			.finally(() => {
				setIsCountryLoading(false);
			});
	}, []);

	useEffect(() => {
		fetchLocationInfo();
	}, []);

	const formMethods = useFormContext();

	const { setValue, watch } = formMethods;

	const countryLabels = useMemo(() => ['name'], []);

	return (
		<>
			<FormItem>
				<Typography noWrap variant="h3" fontWeight="700" gutterBottom>
					stepAwayFirstOrder
				</Typography>
			</FormItem>

			{referralToken && (
				<FormItem>
					{watch('referralAvailable') ? (
						<Typography variant="p1" fontWeight="500" gutterBottom noWrap>
							<Trans
								i18nKey="register.referralText"
								values={{ refereeAmount: `${watch('refereeAmount')} ${watch('currency')}` }}
								components={{
									referralViewer: <ReferralViewer />
								}}
							/>
						</Typography>
					) : (
						<Typography variant="p1" fontWeight="500" gutterBottom noWrap>
							noReferalAvailableCountry
						</Typography>
					)}
				</FormItem>
			)}

			<FormItem label="firstName" required>
				<StringInput placeholder="firstNamePlaceholder" name="firstName" />
			</FormItem>

			<FormItem label="lastName" required>
				<StringInput placeholder="lastNamePlaceholder" name="lastName" />
			</FormItem>
			<FormItem label="register.country" isLoading={isCountryLoading} required>
				<EnumSelect
					name="countryID"
					selectionLabelAccessor={countryLabels}
					value={watch('countryName')}
					placeholder="countryNamePlaceholder"
					renderSelectionValue={(value) => (
						<F.FlagOptionContainer className="selected">
							<span
								className={`oto-country-flag flag-${countries.find((item) => item.name === value)?.code.toLowerCase()}`}
							/>
							<Typography>{value ?? null}</Typography>
						</F.FlagOptionContainer>
					)}
					renderOption={(value) => (
						<F.FlagOptionContainer>
							<span
								className={`oto-country-flag flag-${countries.find((item) => item.name === value)?.code.toLowerCase()}`}
							/>
							<Typography>
								{`country.${countries.find((item) => item.name === value)?.code.toUpperCase()}`}
							</Typography>
						</F.FlagOptionContainer>
					)}
					searchable={false}
					setValue={(value) => {
						setValue(`countryName`, value, {
							shouldValidate: true,
							shouldDirty: true
						});
						const selectedCountry = countries.find((item) => item.name === value);
						setValue(`countryID`, selectedCountry?.ID, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue('countryCode', selectedCountry?.code);
						setValue(`refereeAmount`, selectedCountry?.refereeAmount);
						setValue('currency', selectedCountry?.currency);
						setValue(`referralAvailable`, selectedCountry?.referralAvailable);
					}}
					data={countryNames}
					valueRemovable={false}
					disabled={countries?.length === 1}
				/>
			</FormItem>
			<FormItem label="mobileNumber" required isLoading={isCountryLoading}>
				<PhoneNumber
					name="phone"
					value={watch('phone')}
					onChange={(input) => {
						setValue('phone', input, { shouldValidate: true, shouldDirty: true });
					}}
					countryCode={watch('countryCode')}
				/>
			</FormItem>
			<FormItem label="averageShipmentPerMonth" required>
				<EnumSelect
					name="averageShipmentPerMonth"
					data={['1-10', '10-100', '100-300', '300+', 'avgShipment.noShipment']} // because of the jwt requirement for getEnum endpoint
					value={watch('averageShipmentPerMonth')}
					placeholder="averageShipmentPerMonth"
					searchable={false}
					setValue={(value) => {
						setValue('averageShipmentPerMonth', value, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					valueRemovable={false}
				/>
			</FormItem>
			<FormItem direction="row-reverse" label="haveCompanyName">
				<SwitchInput className="mb-2" name="haveCompanyName" />
			</FormItem>
			{watch('haveCompanyName') ? (
				<FormItem label="companyName" required={watch('haveCompanyName')}>
					<StringInput placeholder="companyNamePlaceholder" name="companyName" />
				</FormItem>
			) : null}
			<FormItem direction="row-reverse" label="havePromoCode">
				<SwitchInput placeholder="havePromoCode" name="havePromoCode" />
			</FormItem>
			{watch('havePromoCode') ? (
				<FormItem label="promoCode">
					<StringInput placeholder="promoCodePlaceholder" name="promoCode" />
				</FormItem>
			) : null}
		</>
	);
};

export default RegisterSecondStep;
